/*
 * Authors: mariushirjanu
 * Created: 26/08/2021
 */
import * as _ from 'underscore';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { GaWizard } from '../../../shared/wizard/ga-wizard';
import { FormattingService } from '../../../shared/services/formatting.service';
import { CompanyDataService, CompanyType } from '../../../shared/company/company-data.service';
import { CountryDataService } from '../../../shared/location/country-data.service';
import { ICodeValue, ICompany, ICompanyExtended, ILocationExtended, IValueObject } from '../../../../type_definitions/datatypes';
import { UntypedFormGroup } from '@angular/forms';
import { GaFormlyService, IExtraFieldConfigurationObject, IFormlyCollapsibleFields } from '../../../shared/formly/ga-formly.service';
import { GaMessageService, IDialog } from '../../../shared/message/ga-message.service';
import { GaValidateService } from '../../../shared/formly/ga-validate.service';
import { GaEventService, IGAEvent } from '../../../shared/services/ga-event.service';
import { Subscription } from 'rxjs';
import { COMPANY_NOT_IN_LIST, DuplicateCompaniesDialogComponent, EDIT_MODE } from './duplicate-companies-dialog.component';
import { CompanyInfoService, ICompanyData } from './company-info.service';
import { EDITOR_TINYMCE, TINYMCE_SIMPLE } from '../../../shared/editor/editor-config.service';
import { TranslationService } from '../../../shared/translation/translation.service';
import { GaStateService } from '../../../shared/services/ga-state.service';
import { BodyClassService } from '../../../shared/services/body-class.service';
import { EVENT_COMPANY_UPDATED, EVENT_DUPLICATE_COMPANY_SELECTED } from '../../../shared/app-events';
import { GaWizardService } from '../../../shared/wizard/ga-wizard.service';
import { WIZARD_COMPANY } from '../../../shared/app-wizards';
import { LedgerDataService } from '../../../shared/services/ledger/ledger-data.service';
import { util } from '../../../shared/util';

interface ICompanyInfoFormFields {
  companyFields:IExtraFieldConfigurationObject[];
  headquarterFields:IExtraFieldConfigurationObject[];
  externalTicketCreationFields:IExtraFieldConfigurationObject[];
  homepageTextFields:IExtraFieldConfigurationObject[];
  configurationLinkFields:IExtraFieldConfigurationObject[];
  companyRelationLinkFields:IExtraFieldConfigurationObject[];
  advancedSettingsFields:IFormlyCollapsibleFields[];
}

@Component({
  selector: 'company-info',
  template: `
    <div class="company-info">

      <div class="row" *ngIf="!loading">
        <div class="col-md-6">
          <h4 class="form-title" translate>APP.COMPANY.HEADINGS.COMPANY_INFO</h4>
          <div class="col-space-bottom">
            <formly [form]="form" [fields]="fields.companyFields" [model]="model" [noValidate]="true" class="form-horizontal"></formly>
          </div>
        </div>

        <div class="col-md-6">
          <h4 class="form-title" translate>APP.COMPANY.HEADINGS.HEADQUARTERS</h4>
          <formly [form]="form" [fields]="fields.headquarterFields" [model]="model" [noValidate]="true" class="form-horizontal"></formly>

          <h4 *ngIf="fields.externalTicketCreationFields" class="form-title" translate>APP.COMPANY.HEADINGS.EXTERNAL_TICKET_CREATION</h4>
          <formly *ngIf="fields.externalTicketCreationFields" [form]="form" [fields]="fields.externalTicketCreationFields" [model]="model" [noValidate]="true" class="form-horizontal"></formly>
        </div>

        <div class="col-md-12 col-space-bottom">
          <h4 class="form-title" translate>APP.COMPANY.HEADINGS.HOMEPAGE_TEXT</h4>
          <formly [form]="form" [fields]="fields.homepageTextFields" [model]="model" [noValidate]="true" class="form-horizontal"></formly>
        </div>

        <div class="col-md-12 col-space-bottom col-space-top" *ngIf="companyId">
          <formly [form]="form" [fields]="fields.configurationLinkFields" [model]="model" [noValidate]="true" class="form-horizontal"></formly>
        </div>

        <div class="col-md-12 col-space-bottom" *ngIf="isCustomerCompany && companyId">
          <h4 class="form-title col-md-12" translate>APP.COMPANY.HEADINGS.RELATIONS</h4>
          <formly [form]="form" [fields]="fields.companyRelationLinkFields" [model]="model" [noValidate]="true" class="form-horizontal"></formly>
        </div>

        <div class="col-md-12 col-space-top">
          <collapsible-pane class="pane-detail" title="{{ 'APP.COMPANY.HEADINGS.ADVANCED_SETTINGS' | translate }}" [collapsed]="advancedSettingsCollapsed" (onChange)="onAdvancedSettingsSectionCollapsed($event)">
            <div class="row">
              <div class="col-md-6">
                <div class="form-horizontal pane-content">
                  <formly [form]="form" [fields]="fields.advancedSettingsFields[0].expanded" [model]="model"></formly>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-horizontal pane-content">
                  <formly [form]="form" [fields]="fields.advancedSettingsFields[1].expanded" [model]="model"></formly>
                </div>
              </div>
            </div>
          </collapsible-pane>
        </div>
      </div>

      <h3 *ngIf="loading">
        <activity-indicator useDefaultText="true"></activity-indicator>
      </h3>
    </div>
  `
})
export class CompanyInfoComponent implements OnInit, OnDestroy {
  @Input() public isInDialog:boolean;

  public form:UntypedFormGroup = new UntypedFormGroup({});
  public model:ICompanyData;
  public fields:ICompanyInfoFormFields;

  public companyId: number | 'NEW';

  private companyDuplicatesDialog:IDialog;
  private backupCompanyName:string;

  private nameField:IExtraFieldConfigurationObject;
  private typeField:IExtraFieldConfigurationObject;
  private gaSkinField:IExtraFieldConfigurationObject;
  private gbdmField:IExtraFieldConfigurationObject;
  private countryField:IExtraFieldConfigurationObject;
  private companyHeadquartersField:IExtraFieldConfigurationObject;
  private ledgerField:IExtraFieldConfigurationObject;

  private eventHandler:Subscription;
  private wizard:GaWizard;

  public loading:boolean;
  public isCustomerCompany:boolean;

  private labelClass:string = 'col-md-3';
  private valueClass:string = 'col-md-9';
  public advancedSettingsCollapsed:boolean = true;

  private relationLabels:string[] = [
    'APP.COMPANY.RELATIONS.LABEL',
    'APP.COMPANY.RELATIONS.PROCUREMENT',
    'APP.COMPANY.RELATIONS.INVOICING',
    'APP.COMPANY.RELATIONS.LESSOR',
    'APP.COMPANY.RELATIONS.SYSTEM_INTEGRATION',
    'APP.COMPANY.RELATIONS.HELPDESK'
  ];
  private linkLabels:string[] = [
    'GA.GENERAL.COUNTRIES',
    'GA.GENERAL.CONTACTS',
    'GA.GENERAL.DOCUMENTS',
    'GA.GENERAL.LOCATIONS',
    'APP.COMPANY.TITLE.ROLES',
    'APP.COMPANY.TITLE.DEPARTMENTS',
    'APP.COMPANY.TITLE.GENERIC_USERS'
  ];

  constructor(
    private bodyClassService:BodyClassService,
    private companyInfoService:CompanyInfoService,
    private companyDataService:CompanyDataService,
    private countryDataService:CountryDataService,
    private eventService:GaEventService,
    private formattingService:FormattingService,
    private formlyService:GaFormlyService,
    private messageService:GaMessageService,
    private gaStateService:GaStateService,
    private translationService:TranslationService,
    private validateService:GaValidateService,
    private wizardService:GaWizardService,
    private ledgerDataService:LedgerDataService
  ) {
    this.wizard = this.wizardService.getInstance(WIZARD_COMPANY);
  }

  public ngOnInit():void {
    this.companyId = this.companyInfoService.getCompanyId();

    this.loading = true;
    this.companyInfoService.getCompanyProfilePromise()
      .then(this.start)
      .then(this.listen)
      .finally(() => {
        this.loading = false;
      });

    if (this.isInDialog) {
      this.gaStateService.screen = 'APP.COMPANY'; // point to the correct screen for loading the correct translations in formly when company-info is loaded inside a dialog (eg. browselist)
    }
  }

  private start = ():void => {
    this.model = this.companyInfoService.getCompanyProfile();

    // when company ID is new or undefined then allow chat with ACSL should be true
    if (this.companyId === 'NEW' || util.isNullOrUndefined(this.companyId)) {
      this.model.allowChatWithACSL = true;
    }

    this.backupCompanyName = this.model.name;
    this.fields = this.createFields();
    this.fillFields();

    // set the current validator to be able to call validateCurrent() in the parent component (company.component.ts)
    this.wizard.currentValidator(this.validateForm, true);
  };

  private listen = ():void => {
    this.eventHandler = this.eventService.events.subscribe((event:IGAEvent) => {
      switch (event.name) {
        case EVENT_COMPANY_UPDATED:
          this.start();
          break;
      }
    });
  };

  private createFields():ICompanyInfoFormFields {
    return {
      companyFields: this.createCompanyFields(),
      headquarterFields: this.createHeadquarterFields(),
      externalTicketCreationFields: this.companyId? this.createExternalTicketCreationFields() : undefined,
      homepageTextFields: this.createHomepageTextFields(),
      configurationLinkFields: this.createConfigurationLinkFields(),
      companyRelationLinkFields: this.createCompanyRelationLinkFields(),
      advancedSettingsFields: this.createAdvancedSettingsFields()
    };
  }

  private createCompanyFields():IExtraFieldConfigurationObject[] {
    return [
      this.nameField = this.formlyService.anInput('name', 'GA.GENERAL.NAME')
        .isRequired()
        .withPlaceholder()
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .onBlur((companyName:string) => {
          if (companyName !== this.backupCompanyName) {
            this.checkCompanyDuplicates(companyName, this.model.type);
          }
        })
        .onMouseOut((companyName) => {
          if (this.nameField.formControl.dirty && companyName !== this.backupCompanyName) {
            this.checkCompanyDuplicates(companyName, this.model.type);
          }
        })
        .withMaxLength(50)
        .build(),

      this.typeField = this.formlyService.aSelect('type', 'GA.GENERAL.TYPE')
        .isRequired()
        .withPlaceholder()
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .withFormat(this.formattingService.formatLov)
        .onChange((type:ICodeValue, oldType:ICodeValue) => {
          if (this.companyId && type && type?.id !== oldType?.id) { // real change
            this.checkCompanyDuplicates(this.model.name, type);
          }
          this.isCustomerCompany = type.id === CompanyType.CUSTOMER;
        })
        .build(),

      this.formlyService.aSearch('group', 'GA.GENERAL.GROUP')
        .withPlaceholder()
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .withFormat(this.formattingService.formatName)
        .withUrl('rest/compman/companygroups/advanced')
        .withExtraOptions({
          templateOptions: {
            minSearchLength: 1
          }
        })
        .build(),

      this.gaSkinField = this.formlyService.aSelect('gaSkin', 'GA.GENERAL.THEME')
        .withPlaceholder()
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .withFormat(this.formattingService.formatLov)
        .build(),

      this.gbdmField = this.formlyService.aSelect('gbdm', 'GBDM')
        .withPlaceholder()
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .withoutEditable()
        .withFormat(this.formattingService.formatContactFN)
        .build(),

      this.formlyService.aTextarea('comment', 'GA.GENERAL.COMMENTS')
        .withPlaceholder()
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .withMaxLength(1000)
        .build()
    ];
  }

  private createHeadquarterFields():IExtraFieldConfigurationObject[] {
    let fields:IExtraFieldConfigurationObject[];

    if (!this.companyId) {
      // create new company fields
      const addressField:IExtraFieldConfigurationObject = this.formlyService.anInput('address', 'GA.GENERAL.ADDRESS')
        .withoutWrapper()
        .withWrapperClass('col-sm-12 col-space-bottom')
        .withPlaceholder()
        .build();
      const zipField:IExtraFieldConfigurationObject = this.formlyService.anInput('zipCode', 'GA.GENERAL.ZIP')
        .withoutWrapper()
        .withWrapperClass('col-4 col-xsm-5 col-sm-4 col-hug-right')
        .withPlaceholder()
        .build();
      const cityField:IExtraFieldConfigurationObject = this.formlyService.anInput('city', 'GA.GENERAL.CITY')
        .withoutWrapper()
        .withWrapperClass('col-8 col-xsm-7 col-sm-8 col-hug-left')
        .withPlaceholder()
        .build();
      this.countryField = this.formlyService.aSelect('country', 'GA.GENERAL.COUNTRY')
        .withPlaceholder()
        .withoutWrapper()
        .withWrapperClass('col-sm-12 col-space-top')
        .withFormat(this.formattingService.formatCountry)
        .build();

      fields = [
        this.formlyService.aGroup(undefined, 'GA.GENERAL.ADDRESS')
          .withLabelClass(this.labelClass)
          .withValueClass(this.valueClass)
          .withFields([
            addressField,
            zipField,
            cityField,
            this.countryField
          ])
          .build(),

        this.formlyService.anInput('vatNumber', this.model.vatName)
          .withLabelClass(this.labelClass)
          .withValueClass(this.valueClass)
          .build()
      ];
    } else {
      // edit company fields
      fields = [
        this.companyHeadquartersField = this.formlyService.aNgSelect('companyHeadquarters', 'GA.GENERAL.LOCATION')
          .withLabelClass(this.labelClass)
          .withValueClass(this.valueClass)
          .withFormat(this.formattingService.formatLocationHtml)
          .withExtraOptions({
            templateOptions: {
              parseHtml: true
            }
          })
          .onChange((selectedLocation:ILocationExtended) => {
            this.model.companyHeadquartersId = selectedLocation?.id;
          })
          .build(),

        this.formlyService.aValue('companyHeadquarters')
          .withLabelClass(this.labelClass)
          .withValueClass(this.valueClass)
          .withFormat((selectedLocation:ILocationExtended) => {
            return this.formattingService.formatLocationHtml(selectedLocation, {
              name: selectedLocation?.companyCountry?.country?.vatName,
              number: selectedLocation?.vatNumber
            });
          })
          .build()
      ];
    }

    return fields;
  }

  private createHomepageTextFields():IExtraFieldConfigurationObject[] {
    return [
      this.formlyService.aTextarea('welcomeText', 'Welcome text')
        .withoutWrapper()
        .withExtraOptions({
          templateOptions: {
            editor: EDITOR_TINYMCE,
            editorCode: TINYMCE_SIMPLE
          }
        })
        .build()
    ];
  }

  private createExternalTicketCreationFields():IExtraFieldConfigurationObject[] {
    return [
      this.formlyService.aValue('urlExternalTicket')
        .withClipboard(true)
        .withoutWrapper()
        .build()
    ];
  }

  private createConfigurationLinkFields():IExtraFieldConfigurationObject[] {
    return [
      this.formlyService.aGroup()
        .withWrapperClass('col-md-4')
        .withLabelClass('d-none')
        .withFields([
          this.formlyService.aLink(this.linkLabels[ 0 ])
            .withExtraOptions({
              templateOptions: {
                href: `#/lists/CC?showfilters=true&filters=Decomnm%09${this.model?.id}&autoload=true&title=${this.translationService.instant(this.linkLabels[ 0 ])}`,
                target: '_blank'
              }
            })
            .withLabelClass('d-none')
            .build(),
          this.formlyService.aLink(this.linkLabels[ 1 ])
            .withExtraOptions({
              templateOptions: {
                href: `#/lists/USERS?showfilters=true&filters=Decomnm%09${this.model?.id}&autoload=true&title=${this.translationService.instant(this.linkLabels[ 1 ])}`,
                target: '_blank'
              }
            })
            .withLabelClass('d-none')
            .build(),
          this.formlyService.aLink(this.linkLabels[ 2 ])
            .withExtraOptions({
              templateOptions: {
                href: `#/unacc?url=%2Funacc%2FAttachView_Browse.jsp%3FDerecid%3D${this.model?.id}%26Detabnm%3DDRCOMPA`,
                target: '_blank'
              }
            })
            .withLabelClass('d-none')
            .build()
        ]).build(),
      this.formlyService.aGroup()
        .withWrapperClass('col-md-4')
        .withLabelClass('d-none')
        .withFields([
          this.formlyService.aLink(this.linkLabels[ 3 ])
            .withExtraOptions({
              templateOptions: {
                href: `#/lists/LOC?filters=Decomnm%09${this.model?.id}&autoload=true&title=${this.translationService.instant(this.linkLabels[ 3 ])}`,
                target: '_blank'
              }
            })
            .withLabelClass('d-none')
            .build(),
          this.formlyService.aLink(this.linkLabels[ 4 ])
            .withExtraOptions({
              templateOptions: {
                href: `#/unacc?url=%2Funacc%2FC_RolesView_Browse.jsp%3FRocomid%3D${this.model?.id}`,
                target: '_blank'
              }
            })
            .withLabelClass('d-none')
            .build()
        ]).build(),
      this.formlyService.aGroup()
        .withWrapperClass('col-md-4')
        .withLabelClass('d-none')
        .withFields([
          this.formlyService.aLink(this.linkLabels[ 5 ])
            .withExtraOptions({
              templateOptions: {
                href: `#/lists/DEPARTMENT_INFO?filters=Decomnm%09${this.model?.id}&autoload=true&title=${this.translationService.instant(this.linkLabels[ 5 ])}`,
                target: '_blank'
              }
            })
            .withLabelClass('d-none')
            .build(),
          this.formlyService.aLink(this.linkLabels[ 6 ])
            .withExtraOptions({
              templateOptions: {
                href: `#/unacc?url=%2Funacc%2FUsrRegView_Browse.jsp%3FDecomid%3D${this.model?.id}%26Deactif%3D1%26Deguser%3D1`,
                target: '_blank'
              }
            })
            .withLabelClass('d-none')
            .build()
        ]).build()
    ];
  }

  private createCompanyRelationLinkFields():IExtraFieldConfigurationObject[] {
    return [
      this.formlyService.aGroup()
        .withWrapperClass('col-md-4')
        .withLabelClass('d-none')
        .withFields(
          [
            this.formlyService.aLink(this.relationLabels[ 1 ])
              .withExtraOptions({
                templateOptions: {
                  href: `#/lists/EXTPREL?title=${this.translationService.instant(this.relationLabels[ 1 ])}%20${this.translationService.instant(this.relationLabels[ 0 ])}&autoload=true&formContext=RELATION_TYPE%09PROCUREMNT&filters=Customer_name%09${this.model?.id}&filters=Relation_type_val%09PROCUREMNT`,
                  target: '_blank'
                }
              })
              .withLabelClass('d-none')
              .build(),
            this.formlyService.aLink(this.relationLabels[ 2 ])
              .withExtraOptions({
                templateOptions: {
                  href: `#/lists/EXTPREL?title=${this.translationService.instant(this.relationLabels[ 2 ])}%20${this.translationService.instant(this.relationLabels[ 0 ])}&autoload=true&formContext=RELATION_TYPE%09INVOICING&filters=Customer_name%09${this.model?.id}&filters=Relation_type_val%09INVOICING`,
                  target: '_blank'
                }
              })
              .withLabelClass('d-none')
              .build()
          ]).build(),
      this.formlyService.aGroup()
        .withWrapperClass('col-md-4')
        .withLabelClass('d-none')
        .withFields(
          [
            this.formlyService.aLink(this.relationLabels[ 3 ])
              .withExtraOptions({
                templateOptions: {
                  href: `#/lists/EXTPREL?title=${this.translationService.instant(this.relationLabels[ 3 ])}%20${this.translationService.instant(this.relationLabels[ 0 ])}&autoload=true&formContext=RELATION_TYPE%09LESSOR&filters=Customer_name%09${this.model?.id}&filters=Relation_type_val%09LESSOR`,
                  target: '_blank'
                }
              })
              .withLabelClass('d-none')
              .build(),
            this.formlyService.aLink(this.relationLabels[ 4 ])
              .withExtraOptions({
                templateOptions: {
                  href: `#/lists/EXTPREL?title=${encodeURIComponent(this.translationService.instant(this.relationLabels[ 4 ]))}%20${this.translationService.instant(this.relationLabels[ 0 ])}&autoload=true&formContext=RELATION_TYPE%09SYSTEM_INT&filters=Customer_name%09${this.model?.id}&filters=Relation_type_val%09SYSTEM_INT`,
                  target: '_blank'
                }
              })
              .withLabelClass('d-none')
              .build()
          ]).build(),
      this.formlyService.aGroup()
        .withWrapperClass('col-md-4')
        .withLabelClass('d-none')
        .withFields(
          [
            this.formlyService.aLink(this.relationLabels[ 5 ])
              .withExtraOptions({
                templateOptions: {
                  href: `#/lists/EXTPREL?title=${this.translationService.instant(this.relationLabels[ 5 ])}%20${this.translationService.instant(this.relationLabels[ 0 ])}&autoload=true&formContext=RELATION_TYPE%09HELPDESK&filters=Customer_name%09${this.model?.id}&filters=Relation_type_val%09HELPDESK`,
                  target: '_blank'
                }
              })
              .withLabelClass('d-none')
              .build()
          ]).build()
    ];
  }

  private createAdvancedSettingsFields():IFormlyCollapsibleFields[] {
    const fields:IFormlyCollapsibleFields[] = [];

    const labelClass1:string = 'col-md-7';
    const valueClass1:string = 'col-md-5';
    const labelClass2:string = 'col-md-5';
    const valueClass2:string = 'col-md-7';

    fields.push({
      expanded: [
        this.formlyService.aCheckbox('allowChatWithACSL')
          .withLabelClass(labelClass1)
          .withValueClass(valueClass1)
          .withFormat(this.formattingService.formatBoolean)
          .withOptions(<IValueObject[]>this.formlyService.getBooleanOptions())
          .build(),

        this.formlyService.aCheckbox('allowChatWithOwnGroup')
          .withLabelClass(labelClass1)
          .withValueClass(valueClass1)
          .withFormat(this.formattingService.formatBoolean)
          .withOptions(<IValueObject[]>this.formlyService.getBooleanOptions())
          .build(),

        this.formlyService.aCheckbox('allowChatWithOwnCompany')
          .withLabelClass(labelClass1)
          .withValueClass(valueClass1)
          .withFormat(this.formattingService.formatBoolean)
          .withOptions(<IValueObject[]>this.formlyService.getBooleanOptions())
          .build(),

        this.formlyService.aCheckbox('allowChatWithBP')
          .withLabelClass(labelClass1)
          .withValueClass(valueClass1)
          .withFormat(this.formattingService.formatBoolean)
          .withOptions(<IValueObject[]>this.formlyService.getBooleanOptions())
          .build(),

        this.formlyService.aCheckbox('allowChatWithCatalogOwners')
          .withLabelClass(labelClass1)
          .withValueClass(valueClass1)
          .withFormat(this.formattingService.formatBoolean)
          .withOptions(<IValueObject[]>this.formlyService.getBooleanOptions())
          .build()
      ]
    });

    fields.push({
      expanded: [
        this.formlyService.aCheckbox('active')
          .withLabelClass(labelClass2)
          .withValueClass(valueClass2)
          .withOptions(<IValueObject[]>this.formlyService.getBooleanOptions())
          .build(),

        this.formlyService.aCheckbox('hideGeneralFaqItems')
          .withLabelClass(labelClass2)
          .withValueClass(valueClass2)
          .withOptions(<IValueObject[]>this.formlyService.getBooleanOptions())
          .build(),

        this.formlyService.aCheckbox('hasSubCompanies')
          .withLabelClass(labelClass2)
          .withValueClass(valueClass2)
          .withOptions(<IValueObject[]>this.formlyService.getBooleanOptions())
          .build(),

        this.formlyService.aCheckbox('subCompanyMandatory')
          .withLabelClass(labelClass2)
          .withValueClass(valueClass2)
          .withOptions(<IValueObject[]>this.formlyService.getBooleanOptions())
          .build(),

        this.formlyService.anInput('subCompanyLabel')
          .withLabelClass(labelClass2)
          .withValueClass(valueClass2)
          .withMaxLength(100)
          .build(),

        this.ledgerField = this.formlyService.aSelect('ledger')
          .withFormat(this.formattingService.formatName)
          .withLabelClass(labelClass2)
          .withValueClass(valueClass2)
          .build()
      ]
    });

    return fields;
  }

  private fillFields():void {
    this.formlyService.fillField(this.typeField, this.companyDataService.getCompanyTypes())
      .then((companyTypes:ICodeValue[]) => {
        if (this.model.type?.id) {
          // fill in the rest of properties
          this.form.patchValue({ type: _.find(companyTypes, { id: this.model.type.id }) });
        }
      });

    this.formlyService.fillField(this.gaSkinField, this.companyDataService.getCompanySkins());
    this.formlyService.fillField(this.gbdmField, this.companyDataService.getCompanyGBDMs());

    if (this.companyId) {
      this.formlyService.fillField(this.companyHeadquartersField, this.companyDataService.getCompanyLocations(<number>this.companyId))
        .then((companyHeadquarters:ILocationExtended[]) => {
          // add timeout to let formly finish its rendering process
          setTimeout(() => {
            this.companyHeadquartersField.formControl.setValue(_.find(companyHeadquarters, { id: this.model.companyHeadquartersId }));
          });
        });
    } else {
      this.formlyService.fillField(this.countryField, this.countryDataService.getAllCountries());
    }

    // get ledgers
    this.formlyService.fillField(this.ledgerField, this.ledgerDataService.getLedgers());
  }

  private validateForm = ():Promise<void | string[]> => {
    const fields:IExtraFieldConfigurationObject[] = this.fields.companyFields.concat(this.fields.headquarterFields);
    return this.validateService.validateFields(fields, this.model, true);
  };

  private checkCompanyDuplicates = (companyName:string, companyType?:ICodeValue) => {
    // do not check for company duplicates when the company-info is already opened in a dialog
    if (!this.isInDialog && companyName?.length > 2) {
      // when editing an existing company filter company duplicates by company name and type; otherwise filter only by name
      const type:string = this.companyId? `${companyType?.id}` : undefined;
      this.companyDataService.searchCompanies(companyName, type)
        .then((comp:ICompany[]) => {
          if (comp && comp.length) {
            const companies:ICompany[] = [];
            _.each(comp, (company:ICompanyExtended) => {
              company.creationDate = this.formattingService.formatDate(company.creationDate);
              companies.push(company);
            });
            this.showCompanyDuplicates(companies, companyName);
          }
        });

      this.backupCompanyName = companyName;
    }
  };

  private showCompanyDuplicates = (companies:ICompany[], companyName:string):void => {
    this.companyDuplicatesDialog = this.messageService.showDialog({
      component: DuplicateCompaniesDialogComponent,
      wrapperComponent: false,
      inputs: {
        mode: this.companyId? EDIT_MODE : undefined,
        results: companies,
        input: companyName
      },
      outputs: {
        onSelected: this.onCompanyDuplicateSelection
      },
      callback: () => {
        this.backupCompanyName = this.model.name;
        this.bodyClassService.add('modal-open'); // add back the class 'modal-open' after closing duplicates dialog, otherwise there is no vertical scroll
      },
      ignoreBackdropClick: true
    });
  };

  private onCompanyDuplicateSelection = (selected:ICompany | string):void => {
    if (selected && selected !== COMPANY_NOT_IN_LIST) {
      this.eventService.sendMessage(EVENT_DUPLICATE_COMPANY_SELECTED, (<ICompany>selected).id);
    }
    this.closeDialog();
  };

  private closeDialog():void {
    this.companyDuplicatesDialog.hide();
  }

  public onAdvancedSettingsSectionCollapsed(collapsed:boolean):void {
    this.advancedSettingsCollapsed = collapsed;
  }

  public ngOnDestroy():void {
    this.wizard.clear();
    this.eventHandler.unsubscribe();
    this.wizard.clearCurrentValidator();
  }
}